import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  getDocs,
  Timestamp,
  where
} from "firebase/firestore";
import { db } from "./firebase";
import { cachedGetDocs } from "./utils/firestoreCache";
import { useNavigate, Link } from "react-router-dom";
import DancerThumbnail from "./DancerThumbnail";
import SponsorshipDisplay from "./components/SponsorshipDisplay";
import { useSponsorship } from "./hooks/useSponsorship";
import { useAdmin } from "./hooks/useAdmin";
import { resolveStudioName } from "./utils/studioNameResolver";
import "./DancerThumbnail.css";
import { getDancerAgeCategoryForYear, shouldResultCountForRankingsInYear, wasInCorrectCategoryAtCompetition } from "./utils/ageCategoryUtils";

// Component to display resolved studio names
function ResolvedStudioName({ originalStudio, dancerName }) {
  const [resolvedName, setResolvedName] = useState(originalStudio);
  
  useEffect(() => {
    resolveStudioName(originalStudio, dancerName).then(setResolvedName);
  }, [originalStudio, dancerName]);
  
  if (!resolvedName || resolvedName === "-") {
    return <span>{resolvedName}</span>;
  }
  
  return (
    <Link 
      to={`/studio/${encodeURIComponent(resolvedName)}`}
      title={originalStudio !== resolvedName ? `Originally: ${originalStudio}` : ''}
      onClick={(e) => e.stopPropagation()}
      style={{ 
        cursor: 'pointer',
        color: 'var(--accent-color)',
        textDecoration: 'underline'
      }}
    >
      {resolvedName}
    </Link>
  );
}

const latinCategories = [
  "Solo Juvenile I", "Solo Juvenile II", "Solo Junior I",
  "Solo Junior II", "Solo Youth", "Solo Adult"
];

const dances = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];

const contemporaryCategories = [
  "Solo Contemporary Juvenile I", "Solo Contemporary Juvenile II",
  "Solo Contemporary Junior I", "Solo Contemporary Junior II",
  "Solo Contemporary Youth", "Solo Contemporary Adult"
];

const hipHopCategories = [
  "Solo Hip Hop Juvenile I", "Solo Hip Hop Juvenile II",
  "Solo Hip Hop Junior I", "Solo Hip Hop Junior II",
  "Solo Hip Hop Youth", "Solo Hip Hop Adult"
];

const commercialCategories = [
  "Solo Commercial Juvenile I", "Solo Commercial Juvenile II",
  "Solo Commercial Junior I", "Solo Commercial Junior II",
  "Solo Commercial Youth", "Solo Commercial Adult"
];

/**
 * Load all studio transfers and return a map of dancerName -> array of transfers (sorted newest first)
 */
async function loadStudioTransfers() {
  try {
    const transfersSnapshot = await getDocs(
      query(collection(db, 'studioTransfers'), where('status', '==', 'active'))
    );
    
    const transfersByDancer = new Map();
    
    transfersSnapshot.docs.forEach(doc => {
      const transfer = doc.data();
      const transferDate = transfer.transferDate?.toDate ? transfer.transferDate.toDate() : new Date(transfer.transferDate);
      
      if (!transfersByDancer.has(transfer.dancerName)) {
        transfersByDancer.set(transfer.dancerName, []);
      }
      transfersByDancer.get(transfer.dancerName).push({
        ...transfer,
        transferDate
      });
    });
    
    // Sort transfers for each dancer by date (most recent first)
    transfersByDancer.forEach((transfers) => {
      transfers.sort((a, b) => b.transferDate - a.transferDate);
    });
    
    return transfersByDancer;
  } catch (error) {
    console.error('❌ Error loading studio transfers:', error);
    return new Map();
  }
}

export default function EternalCupRanking() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedDiscipline, setSelectedDiscipline] = useState("Latin");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [rankings, setRankings] = useState([]);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const { isSponsored } = useSponsorship();
  const { isAdmin } = useAdmin();

  // Get available categories based on selected discipline
  const getAvailableCategories = () => {
    switch (selectedDiscipline) {
      case "Latin":
        return latinCategories;
      case "Contemporary":
        return contemporaryCategories;
      case "Hip Hop":
        return hipHopCategories;
      case "Commercial":
        return commercialCategories;
      default:
        return [];
    }
  };

  // Get available years from competitions
  const getAvailableYears = async () => {
    const years = new Set();
    
    try {
      // Query old structure competitions
      const oldQuery = ["competitions"];
      const oldSnapshot = await cachedGetDocs(...oldQuery);
      
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.timestamp) {
          const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
          years.add(compDate.getFullYear());
        }
      });

      // Query new structure competitions (whole tree fetched concurrently, results are cached)
      const addYears = (snapshot) => snapshot.forEach((d) => {
        const comp = d.data();
        if (comp.timestamp) {
          const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
          years.add(compDate.getFullYear());
        }
      });
      const safe = (promise) => promise.catch(() => null); // Collection might not exist

      const disciplinesSnapshot = await cachedGetDocs("disciplines");
      await Promise.all(disciplinesSnapshot.docs.map(async (disciplineDoc) => {
        const categoriesSnapshot = await cachedGetDocs("disciplines", disciplineDoc.id, "categories");
        await Promise.all(categoriesSnapshot.docs.map(async (categoryDoc) => {
          const base = ["disciplines", disciplineDoc.id, "categories", categoryDoc.id];
          const [directComps, dancesSnapshot] = await Promise.all([
            safe(cachedGetDocs(...base, "competitions")),
            safe(cachedGetDocs(...base, "dances")),
          ]);
          if (directComps) addYears(directComps);
          if (dancesSnapshot) {
            const danceComps = await Promise.all(
              dancesSnapshot.docs.map(danceDoc => safe(cachedGetDocs(...base, "dances", danceDoc.id, "competitions")))
            );
            danceComps.forEach(snap => { if (snap) addYears(snap); });
          }
        }));
      }));
    } catch (error) {
      console.error("❌ Error fetching available years:", error);
    }

    const sortedYears = Array.from(years).sort((a, b) => b - a); // Most recent first
    setAvailableYears(sortedYears);
    
    // If current year is not in available years, default to most recent year
    if (sortedYears.length > 0 && !sortedYears.includes(selectedYear)) {
      setSelectedYear(sortedYears[0]);
    }
  };

  // Calculate rankings for selected year competitions
  const calculateEternalCupRankings = async () => {
    if (!selectedDiscipline || !selectedCategory || !selectedYear) {
      setRankings([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const yearStart = new Date(selectedYear, 0, 1); // January 1st of selected year
      const yearEnd = new Date(selectedYear + 1, 0, 1); // January 1st of next year

      console.log(`🔍 Calculating Eternal Cup Rankings for ${selectedDiscipline} - ${selectedCategory} - ${selectedYear}`);
      console.log(`🔍 Year range: ${yearStart.toDateString()} to ${yearEnd.toDateString()}`);

      const dancersMap = new Map();
      const dancerAgeCategories = {}; // Cache for age categories
      const allDancerNames = new Set(); // Collect all dancer names first

      // Step 1: Collect all dancer names from old structure competitions
      const oldQuery = ["competitions"];
      const oldSnapshot = await cachedGetDocs(...oldQuery);

      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (!comp.results || !comp.timestamp) return;

        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
        
        // Only include competitions from selected year
        if (compDate < yearStart || compDate >= yearEnd) return;

        // Check if this competition matches our criteria
        const matchesDiscipline = comp.discipline === selectedDiscipline;
        const matchesCategory = comp.category === selectedCategory;
        
        if (!matchesDiscipline || !matchesCategory) return;

        // Collect dancer names
        if (Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            if (result.dancerName && !result.excluded && !result.autoExcluded && !result.invalid) {
              allDancerNames.add(result.dancerName);
            }
          });
        }
      });

      // Step 2: Collect dancer names from new structure competitions
      const allDisciplinesSnapshot = await cachedGetDocs("disciplines");
      
      for (const disciplineDoc of allDisciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        if (disciplineName !== selectedDiscipline) continue;
        
        const categoriesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories");
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          if (categoryName !== selectedCategory) continue;
          
          try {
            // For Latin discipline, collect from dances subcollection
            if (selectedDiscipline === "Latin") {
              const dancesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances");
              await Promise.all(dancesSnapshot.docs.map(d => cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances", d.id, "competitions")));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                
                const danceCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"];
                const danceCompsSnapshot = await cachedGetDocs(...danceCompsQuery);
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (!comp.results || !comp.timestamp) return;

                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  
                  // Only include competitions from selected year
                  if (compDate < yearStart || compDate >= yearEnd) return;

                  if (Array.isArray(comp.results)) {
                    comp.results.forEach((result) => {
                      if (result.dancerName && !result.excluded && !result.autoExcluded && !result.invalid) {
                        allDancerNames.add(result.dancerName);
                      }
                    });
                  }
                });
              }
            } else {
              // For non-Latin disciplines, collect from direct competitions
              const directCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "competitions"];
              const directCompsSnapshot = await cachedGetDocs(...directCompsQuery);
              
              directCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (!comp.results || !comp.timestamp) return;

                const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                
                // Only include competitions from selected year
                if (compDate < yearStart || compDate >= yearEnd) return;

                if (Array.isArray(comp.results)) {
                  comp.results.forEach((result) => {
                    if (result.dancerName && !result.excluded && !result.autoExcluded && !result.invalid) {
                      allDancerNames.add(result.dancerName);
                    }
                  });
                }
              });
            }
          } catch (error) {
            console.log("🔍 No competitions found for", disciplineName, categoryName);
          }
        }
      }

      // Step 3: Get age categories for all collected dancers for the selected year
      console.log(`🎂 Getting age categories for ${allDancerNames.size} dancers for year ${selectedYear}`);
      await Promise.all(
        Array.from(allDancerNames).map(async (name) => {
          dancerAgeCategories[name] = await getDancerAgeCategoryForYear(name, selectedYear);
        })
      );

      // Step 3.5: Load studio transfers to show correct studio for the selected year
      console.log(`🔄 Loading studio transfers for year ${selectedYear}`);
      const studioTransfersMap = await loadStudioTransfers();
      console.log(`🔄 Found ${studioTransfersMap.size} dancers with studio transfers`);
      
      // Helper function to get studio for a dancer at the end of the selected year
      const getStudioForYear = (dancerName, fallbackStudio) => {
        const transfers = studioTransfersMap.get(dancerName);
        if (!transfers || transfers.length === 0) {
          // No transfers - use fallback from competition result
          return fallbackStudio || "-";
        }
        
        // Find the most recent transfer that happened before or during the selected year
        const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59); // Dec 31 of selected year
        
        for (const transfer of transfers) {
          // If transfer happened before or during the selected year, use this studio
          if (transfer.transferDate <= yearEnd) {
            console.log(`📍 ${dancerName}: Using transferred studio "${transfer.toStudio}" (transfer on ${transfer.transferDate.toLocaleDateString()})`);
            return transfer.toStudio;
          }
        }
        
        // If all transfers are after the selected year, use the fromStudio of the earliest transfer
        // (meaning the dancer was at their original studio during the selected year)
        const earliestTransfer = transfers[transfers.length - 1];
        const studioBeforeTransfer = earliestTransfer.fromStudio || fallbackStudio || "-";
        console.log(`📍 ${dancerName}: All transfers after ${selectedYear}, using pre-transfer studio "${studioBeforeTransfer}"`);
        return studioBeforeTransfer;
      };

      // Step 4: Process results with age category filtering (old structure)
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (!comp.results || !comp.timestamp) return;

        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
        
        // Only include competitions from selected year
        if (compDate < yearStart || compDate >= yearEnd) return;

        // Check if this competition matches our criteria
        const matchesDiscipline = comp.discipline === selectedDiscipline;
        const matchesCategory = comp.category === selectedCategory;
        
        if (!matchesDiscipline || !matchesCategory) return;

        // For Latin discipline, we combine all dances (like Eternal Cup Winners logic)
        if (selectedDiscipline === "Latin") {
          // Process all Latin dances for this category
          comp.results.forEach((result) => {
            // Skip excluded, auto-excluded, or invalid results 
            // Manual exclusions only for current year, auto-exclusions always apply
            const currentYear = new Date().getFullYear();
            const shouldSkipDueToExclusion = (result.excluded && selectedYear === currentYear) || result.autoExcluded || result.invalid;
            if (shouldSkipDueToExclusion) return;
            
            const dancerName = result.dancerName;
            
            // Check if this result should count for rankings based on age category for the selected year
            const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
            if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
              console.log(`⏭️ Skipping ${dancerName}'s result from category ${selectedCategory} (age category in ${selectedYear}: ${ageCategoryInYear})`);
              return;
            }
            
            // Check if dancer was in the CORRECT category at the time of competition
            // (filter out results where dancer competed in wrong age category)
            if (!wasInCorrectCategoryAtCompetition(selectedCategory, ageCategoryInYear)) {
              console.log(`⏭️ Skipping ${dancerName}'s result - competed in ${selectedCategory} but should have been in ${ageCategoryInYear} at time of competition (${selectedYear})`);
              return;
            }
            
            if (!dancersMap.has(dancerName)) {
              // Use studio for the selected year (respecting transfers)
              const studioForYear = getStudioForYear(dancerName, result.studio);
              dancersMap.set(dancerName, {
                name: dancerName,
                studio: studioForYear,
                results: [],
                totalPoints: 0
              });
            }

            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            dancersMap.get(dancerName).results.push({
              compId: doc.id,
              compName: comp.name || doc.id,
              dance: comp.dance || "Unknown",
              points: score,
              place: result.place,
              timestamp: comp.timestamp,
              participants: comp.participants || 0
            });
          });
        } else {
          // For non-Latin disciplines, process normally
          comp.results.forEach((result) => {
            // Skip excluded, auto-excluded, or invalid results 
            // Manual exclusions only for current year, auto-exclusions always apply
            const currentYear = new Date().getFullYear();
            const shouldSkipDueToExclusion = (result.excluded && selectedYear === currentYear) || result.autoExcluded || result.invalid;
            if (shouldSkipDueToExclusion) return;
            
            const dancerName = result.dancerName;
            
            // Check if this result should count for rankings based on age category for the selected year
            const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
            if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
              console.log(`⏭️ Skipping ${dancerName}'s result from category ${selectedCategory} (age category in ${selectedYear}: ${ageCategoryInYear})`);
              return;
            }
            
            // Check if dancer was in the CORRECT category at the time of competition
            // (filter out results where dancer competed in wrong age category)
            if (!wasInCorrectCategoryAtCompetition(selectedCategory, ageCategoryInYear)) {
              console.log(`⏭️ Skipping ${dancerName}'s result - competed in ${selectedCategory} but should have been in ${ageCategoryInYear} at time of competition (${selectedYear})`);
              return;
            }
            
            if (!dancersMap.has(dancerName)) {
              // Use studio for the selected year (respecting transfers)
              const studioForYear = getStudioForYear(dancerName, result.studio);
              dancersMap.set(dancerName, {
                name: dancerName,
                studio: studioForYear,
                results: [],
                totalPoints: 0
              });
            }

            const score = Math.round((result.points || 0) * (comp.multiplier || 1));
            dancersMap.get(dancerName).results.push({
              compId: doc.id,
              compName: comp.name || doc.id,
              points: score,
              place: result.place,
              timestamp: comp.timestamp,
              participants: comp.participants || 0
            });
          });
        }
      });

      // Query new structure competitions
      const disciplinesSnapshot = await cachedGetDocs("disciplines");
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        if (disciplineName.toLowerCase().replace(/[^a-z0-9]+/g, "_") !== selectedDiscipline.toLowerCase().replace(/[^a-z0-9]+/g, "_")) continue;

        const categoriesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories");
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          const originalCategory = selectedCategory.split(" ").slice(-2).join("_").toLowerCase();
          if (categoryName !== originalCategory) continue;

          // For Latin discipline, query all dances and combine results
          if (selectedDiscipline === "Latin") {
            try {
              const dancesSnapshot = await cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances");
              await Promise.all(dancesSnapshot.docs.map(d => cachedGetDocs("disciplines", disciplineName, "categories", categoryName, "dances", d.id, "competitions")));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                
                const danceCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"];
                const danceCompsSnapshot = await cachedGetDocs(...danceCompsQuery);
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (!comp.results || !comp.timestamp) return;

                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  
                  // Only include competitions from selected year
                  if (compDate < yearStart || compDate >= yearEnd) return;

                  comp.results.forEach((result) => {
                    // Skip excluded, auto-excluded, or invalid results 
                    // Manual exclusions only for current year, auto-exclusions always apply
                    const currentYear = new Date().getFullYear();
                    const shouldSkipDueToExclusion = (result.excluded && selectedYear === currentYear) || result.autoExcluded || result.invalid;
                    if (shouldSkipDueToExclusion) return;
                    
                    const dancerName = result.dancerName;
                    
                    // Check if this result should count for rankings based on age category for the selected year
                    const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
                    if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
                      console.log(`⏭️ Skipping ${dancerName}'s result from category ${selectedCategory} (age category in ${selectedYear}: ${ageCategoryInYear})`);
                      return;
                    }
                    
                    // Check if dancer was in the CORRECT category at the time of competition
                    if (!wasInCorrectCategoryAtCompetition(selectedCategory, ageCategoryInYear)) {
                      console.log(`⏭️ Skipping ${dancerName}'s result - competed in ${selectedCategory} but should have been in ${ageCategoryInYear} (${selectedYear})`);
                      return;
                    }
                    
                    if (!dancersMap.has(dancerName)) {
                      // Use studio for the selected year (respecting transfers)
                      const studioForYear = getStudioForYear(dancerName, result.studio);
                      dancersMap.set(dancerName, {
                        name: dancerName,
                        studio: studioForYear,
                        results: [],
                        totalPoints: 0
                      });
                    }

                    const score = Math.round((result.points || 0) * (comp.multiplier || 1));
                    dancersMap.get(dancerName).results.push({
                      compId: doc.id,
                      compName: comp.name || doc.id,
                      dance: danceName,
                      points: score,
                      place: result.place,
                      timestamp: comp.timestamp,
                      participants: comp.participants || 0
                    });
                  });
                });
              }
            } catch (error) {
              console.log("🔍 No dances found for", disciplineName, categoryName);
            }
          } else {
            // For non-Latin disciplines, query direct competitions
            try {
              const directCompsQuery = ["disciplines", disciplineName, "categories", categoryName, "competitions"];
              const directCompsSnapshot = await cachedGetDocs(...directCompsQuery);
              
              directCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (!comp.results || !comp.timestamp) return;

                const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                
                // Only include competitions from selected year
                if (compDate < yearStart || compDate >= yearEnd) return;

                comp.results.forEach((result) => {
                  // Skip excluded, auto-excluded, or invalid results 
                  // Manual exclusions only for current year, auto-exclusions always apply
                  const currentYear = new Date().getFullYear();
                  const shouldSkipDueToExclusion = (result.excluded && selectedYear === currentYear) || result.autoExcluded || result.invalid;
                  if (shouldSkipDueToExclusion) return;
                  
                  const dancerName = result.dancerName;
                  
                  // Check if this result should count for rankings based on age category for the selected year
                  const ageCategoryInYear = dancerAgeCategories[dancerName] ?? null;
                  if (!shouldResultCountForRankingsInYear(selectedCategory, ageCategoryInYear)) {
                    console.log(`⏭️ Skipping ${dancerName}'s result from category ${selectedCategory} (age category in ${selectedYear}: ${ageCategoryInYear})`);
                    return;
                  }
                  
                  // Check if dancer was in the CORRECT category at the time of competition
                  if (!wasInCorrectCategoryAtCompetition(selectedCategory, ageCategoryInYear)) {
                    console.log(`⏭️ Skipping ${dancerName}'s result - competed in ${selectedCategory} but should have been in ${ageCategoryInYear} (${selectedYear})`);
                    return;
                  }
                  
                  if (!dancersMap.has(dancerName)) {
                    // Use studio for the selected year (respecting transfers)
                    const studioForYear = getStudioForYear(dancerName, result.studio);
                    dancersMap.set(dancerName, {
                      name: dancerName,
                      studio: studioForYear,
                      results: [],
                      totalPoints: 0
                    });
                  }

                  const score = Math.round((result.points || 0) * (comp.multiplier || 1));
                  dancersMap.get(dancerName).results.push({
                    compId: doc.id,
                    compName: comp.name || doc.id,
                    points: score,
                    place: result.place,
                    timestamp: comp.timestamp,
                    participants: comp.participants || 0
                  });
                });
              });
            } catch (error) {
              console.log("🔍 No direct competitions for", disciplineName, categoryName);
            }
          }
        }
      }

      // Calculate total points for each dancer
      // For Latin discipline: topN results from each of the 5 dances combined (topN=6 from 2026, 4 before)
      // For other disciplines: use best topN results
      const topN = selectedYear >= 2026 ? 6 : 4;
      const dancersWithPoints = Array.from(dancersMap.values()).map((dancer) => {
        if (selectedDiscipline === "Latin") {
          // For Latin: topN results from each dance, combined together
          const danceResults = {};
          
          // Group results by dance
          dancer.results.forEach((result) => {
            const dance = result.dance || "Unknown";
            if (!danceResults[dance]) {
              danceResults[dance] = [];
            }
            danceResults[dance].push(result);
          });
          
          // Get topN results from each dance and combine them
          let allTopResults = [];
          Object.entries(danceResults).forEach(([dance, results]) => {
            const sortedDanceResults = results.sort((a, b) => b.points - a.points);
            const topFromDance = sortedDanceResults.slice(0, topN);
            allTopResults = allTopResults.concat(topFromDance);
          });
          
          // Sort all combined results and calculate total points
          allTopResults.sort((a, b) => b.points - a.points);
          const totalPoints = allTopResults.reduce((sum, result) => sum + result.points, 0);
          const danceCount = Object.keys(danceResults).length;
          
          return {
            ...dancer,
            totalPoints,
            competitionCount: dancer.results.length,
            danceCount,
            bestResults: allTopResults,
            danceBreakdown: Object.entries(danceResults).map(([dance, results]) => ({
              dance,
              count: results.length,
              topN: results.sort((a, b) => b.points - a.points).slice(0, topN)
            }))
          };
        } else {
          // For non-Latin: best topN results
          const sortedResults = dancer.results.sort((a, b) => b.points - a.points);
          const topResults = sortedResults.slice(0, topN);
          const totalPoints = topResults.reduce((sum, result) => sum + result.points, 0);
          
          return {
            ...dancer,
            totalPoints,
            competitionCount: dancer.results.length,
            bestResults: topResults
          };
        }
      });

      // Sort by total points and assign rankings with tie handling
      const sortedByPoints = dancersWithPoints
        .filter(dancer => dancer.totalPoints > 0)
        .sort((a, b) => b.totalPoints - a.totalPoints);
      
      // Assign ranks with proper tie handling
      let lastPoints = null;
      let lastRank = 0;
      let tieCount = 0;
      
      const sortedDancers = sortedByPoints.map((dancer) => {
        if (lastPoints === dancer.totalPoints) {
          // Same points as previous dancer = same rank
          tieCount++;
          return { ...dancer, rank: lastRank };
        } else {
          // Different points = new rank (accounting for ties)
          const newRank = lastRank + tieCount + 1;
          lastRank = newRank;
          lastPoints = dancer.totalPoints;
          tieCount = 0;
          return { ...dancer, rank: newRank };
        }
      });

      console.log(`🔍 Found ${sortedDancers.length} dancers for Eternal Cup Ranking`);
      setRankings(sortedDancers);

    } catch (error) {
      console.error("❌ Error calculating Eternal Cup Rankings:", error);
      setError(`Error loading rankings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format date helper
  const formatDate = (timestamp) => {
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // Handle dancer name click
  const handleDancerClick = (dancerName) => {
    navigate(`/dancer/${encodeURIComponent(dancerName)}`);
  };

  useEffect(() => {
    // Load available years shortly after mount, so the first ranking gets the bandwidth first
    const timer = setTimeout(getAvailableYears, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (selectedDiscipline && selectedCategory && selectedYear) {
      calculateEternalCupRankings();
    } else {
      setRankings([]);
      setLoading(false);
    }
  }, [selectedDiscipline, selectedCategory, selectedYear]);

  const currentYear = new Date().getFullYear();

  return (
    <div style={{ padding: "1rem", maxWidth: "none", margin: "0" }}>
      <h2>🏆 Eternal Cup Ranking {selectedYear}</h2>
      <p style={{ fontSize: "1.1em", color: "#666", marginBottom: "2rem" }}>
        <strong>Ranking Logic:</strong> Points calculated from competitions within the selected year (January 1 - December 31, {selectedYear}).<br/>
        {selectedDiscipline === "Latin" 
          ? (selectedYear >= 2026
            ? `For Latin discipline: Top 6 results from each of the 5 dances combined together (up to 30 total results).`
            : `For Latin discipline: Top 4 results from each of the 5 dances combined together (up to 20 total results).`)
          : (selectedYear >= 2026
            ? `For other disciplines: Best 6 results from selected year competitions.`
            : `For other disciplines: Best 4 results from selected year competitions.`)
        }
      </p>

      {/* Discipline, Category, and Year Selection */}
      <div style={{ 
        display: "flex", 
        gap: "1rem", 
        marginBottom: "2rem", 
        padding: "1rem", 
        backgroundColor: "#f5f5f5", 
        borderRadius: "5px",
        border: "1px solid #ddd",
        flexWrap: "wrap"
      }}>
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Year:</strong>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
            disabled={availableYears.length === 0}
          >
            {availableYears.length === 0 ? (
              <option value="">Loading years...</option>
            ) : (
              availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))
            )}
          </select>
        </label>
        
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Discipline:</strong>
          <select 
            value={selectedDiscipline} 
            onChange={(e) => {
              setSelectedDiscipline(e.target.value);
              setSelectedCategory(""); // Reset category when discipline changes
            }}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="Latin">Latin</option>
            <option value="Contemporary">Contemporary</option>
            <option value="Hip Hop">Hip Hop</option>
            <option value="Commercial">Commercial</option>
          </select>
        </label>
        
        <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
          <strong>Age Category:</strong>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="">Select Category</option>
            {getAvailableCategories().map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ 
          color: "#d32f2f", 
          backgroundColor: "#ffebee", 
          padding: "1rem", 
          borderRadius: "5px", 
          marginBottom: "1rem" 
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading rankings...</p>
        </div>
      )}

      {/* No Selection State */}
      {!selectedDiscipline || !selectedCategory || !selectedYear ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>Please select year, discipline and category to view rankings.</p>
        </div>
      ) : null}

      {/* Rankings Display */}
      {!loading && selectedDiscipline && selectedCategory && selectedYear && rankings.length > 0 && (
        <div>
          <h3>Rankings for {selectedDiscipline} - {selectedCategory} ({selectedYear})</h3>
          <p style={{ fontSize: "0.9em", color: "#666", marginBottom: "1rem" }}>
            Showing {rankings.length} dancers with competition results from {selectedYear}
          </p>
          
          {/* Desktop Header */}
          <div className="leaderboard-header">
            <div style={{ textAlign: "center" }}>Rank</div>
            <div></div> {/* Space for profile picture */}
            <div>Dancer</div>
            <div style={{ textAlign: "center" }}>Points</div>
            {isAdmin && <div style={{ textAlign: "center" }}>Competitions</div>}
          </div>

          {/* Responsive Dancer Rows */}
          {rankings.map((dancer) => {
            // Define border colors for top 3
            let borderColor = "#e0e0e0";
            if (dancer.rank === 1) borderColor = "#ffd700"; // Gold
            else if (dancer.rank === 2) borderColor = "#c0c0c0"; // Silver
            else if (dancer.rank === 3) borderColor = "#cd7f32"; // Bronze

            return (
              <div 
                key={dancer.name}
                className="leaderboard-row"
                style={{
                  backgroundColor: dancer.rank <= 3 ? "#fff3e0" : "white",
                  borderColor: borderColor
                }}
              >
                {/* Rank and Points */}
                <div className="leaderboard-place">
                  <div className="place-badge">
                    {dancer.rank === 1 && "🥇"}
                    {dancer.rank === 2 && "🥈"}
                    {dancer.rank === 3 && "🥉"}
                    {dancer.rank > 3 && `#${dancer.rank}`}
                  </div>
                  <div className="points-under-place">
                    <span className="mobile-label">Points:</span>
                    <span className="points-value">{dancer.totalPoints}</span>
                  </div>
                </div>

                {/* Profile Picture */}
                <div className="leaderboard-photo">
                  <DancerThumbnail 
                    dancerName={dancer.name} 
                    size={50} 
                    showPlaceholder={true}
                  />
                </div>

                {/* Dancer Info - More space for names */}
                <div className="leaderboard-dancer-info">
                  <div className="dancer-name">
                    <Link 
                      to={`/dancer/${encodeURIComponent(dancer.name)}`}
                      style={{
                        cursor: "pointer",
                        color: "#007bff",
                        textDecoration: "underline",
                        fontWeight: "bold"
                      }}
                    >
                      {dancer.name}
                    </Link>
                    <SponsorshipDisplay 
                      dancerName={dancer.name}
                      isSponsored={isSponsored(dancer.name)}
                      size="small"
                      layout="horizontal"
                      position="inline-right"
                    />
                  </div>
                  <div className="dancer-studio">
                    <ResolvedStudioName originalStudio={dancer.studio} dancerName={dancer.name} />
                  </div>
                </div>

                {/* Points - Desktop Only (Mobile shows under rank) */}
                <div className="leaderboard-points desktop-only">
                  <span className="points-value">{dancer.totalPoints}</span>
                </div>

                {/* Competitions - Admin Only */}
                {isAdmin && (
                  <div className="leaderboard-competitions">
                    <span className="mobile-label">Competitions:</span>
                    <span className="competitions-value">
                      {selectedDiscipline === "Latin" 
                        ? `${dancer.danceCount}/5 dances (${dancer.bestResults.length} results)` 
                        : `${dancer.competitionCount} total`
                      }
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No Results State */}
      {!loading && selectedDiscipline && selectedCategory && selectedYear && rankings.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>No competition results found for {selectedDiscipline} - {selectedCategory} in {selectedYear}.</p>
        </div>
      )}
    </div>
  );
}
